import { Fragment } from 'react';
import {
  presentDocumentReferenceDocument,
  type PresentedDocumentReferenceBlock,
  type PresentedDocumentReferenceItem,
} from './document-reference-render-model';
import type {
  DocumentReferenceCallout,
  DocumentReferenceSectionContent,
  DocumentReferenceStructuredContentPayload,
  DocumentReferenceStructuredSection,
} from './document-reference-types';

interface DocumentReferenceStructuredContentProps {
  content: DocumentReferenceStructuredContentPayload;
}

const splitInlineSegments = (text: string) =>
  text.split(/(".*?")/g).filter((segment) => segment.length > 0);

const InlineFormattedText = ({ text }: { text: string }) => (
  <>
    {splitInlineSegments(text).map((segment, idx) =>
      segment.startsWith('"') && segment.endsWith('"') ? (
        <strong key={`inline-segment-${idx}`}>{segment}</strong>
      ) : (
        <Fragment key={`inline-segment-${idx}`}>{segment}</Fragment>
      ),
    )}
  </>
);

const CalloutParagraph = ({
  callout,
}: {
  callout: DocumentReferenceCallout;
}) => {
  const placementClass =
    callout.placement === 'trailing'
      ? 'text-slate-500 italic'
      : 'text-slate-600';

  return (
    <p
      className={`text-[10px] leading-tight whitespace-pre-line ${placementClass}`}
      data-style={callout.styleCode}
      data-id={callout.id}
    >
      <InlineFormattedText text={callout.text} />
    </p>
  );
};

const SectionHeading = ({
  section,
}: {
  section: DocumentReferenceStructuredSection;
}) => {
  if (!section.heading) return null;

  return (
    <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
      {section.heading}
    </p>
  );
};

const SectionContent = ({
  content,
}: {
  content: DocumentReferenceSectionContent;
}) => {
  if (content.kind === 'paragraph') {
    return (
      <p className="text-[10px] leading-tight whitespace-pre-line text-zinc-950">
        <InlineFormattedText text={content.text} />
      </p>
    );
  }

  if (content.kind === 'list') {
    return (
      <ul className="list-disc space-y-1 pl-5 text-[10px] leading-tight text-zinc-950">
        {content.items.map((item, idx) => (
          <li key={`docref-list-item-${idx}`}>
            <InlineFormattedText text={item} />
          </li>
        ))}
      </ul>
    );
  }

  if (content.kind === 'keyValue') {
    return (
      <div className="overflow-hidden rounded-sm border border-zinc-200">
        {content.entries.map((entry, idx) => (
          <div
            key={`docref-kv-${idx}`}
            className={`flex border-t border-zinc-200 first:border-t-0 ${
              idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'
            }`}
          >
            <div className="flex flex-1 border-r border-zinc-200 px-3 py-2">
              <p className="flex-1 text-[10px] leading-tight font-semibold whitespace-pre-line text-zinc-700">
                {entry.key}
              </p>
            </div>
            <div className="flex flex-1 px-3 py-2">
              <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
                {entry.value ?? ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (content.kind === 'table') {
    return (
      <div className="overflow-hidden rounded-sm border border-zinc-200">
        <table className="w-full border-collapse text-[10px] leading-tight">
          <thead>
            <tr className="bg-slate-100">
              <th className="border-r border-zinc-300 px-3 py-2 text-left font-black tracking-[0.35px] text-zinc-500 uppercase">
                Test
              </th>
              <th className="border-r border-zinc-300 px-3 py-2 text-left font-black tracking-[0.35px] text-zinc-500 uppercase">
                Unit
              </th>
              <th className="px-3 py-2 text-left font-black tracking-[0.35px] text-zinc-500 uppercase">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {content.rows.map((row, idx) => (
              <tr
                key={`docref-table-row-${idx}`}
                className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
              >
                <td className="border-t border-r border-zinc-200 px-3 py-2 text-zinc-950">
                  {row.test}
                </td>
                <td className="border-t border-r border-zinc-200 px-3 py-2 text-zinc-700">
                  {row.unit ?? ''}
                </td>
                <td className="border-t border-zinc-200 px-3 py-2 text-zinc-950">
                  {row.value}
                  {row.flagged ? '*' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-sm border border-zinc-200">
      {content.entries.map((entry, idx) => (
        <div
          key={`docref-vitals-${idx}`}
          className={`flex border-t border-zinc-200 first:border-t-0 ${
            idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'
          }`}
        >
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2">
            <p className="flex-1 text-[10px] leading-tight font-semibold text-zinc-700">
              {entry.name}
            </p>
          </div>
          <div className="flex flex-1 px-3 py-2">
            <p className="flex-1 text-[10px] leading-tight text-zinc-950">
              {entry.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const StructuredBlock = ({
  block,
  index,
}: {
  block: PresentedDocumentReferenceBlock;
  index: number;
}) => (
  <div className="flex flex-col gap-3">
    {block.leadingCallouts.map((callout, idx) => (
      <CalloutParagraph
        key={`docref-leading-${block.id ?? index}-${idx}`}
        callout={callout}
      />
    ))}

    {block.sections.map((section, sectionIdx) => (
      <div
        key={`docref-section-${block.id ?? index}-${sectionIdx}`}
        className="flex flex-col gap-2"
      >
        <SectionHeading section={section} />
        {section.contents.map((content, contentIdx) => (
          <SectionContent
            key={`docref-section-content-${sectionIdx}-${contentIdx}`}
            content={content}
          />
        ))}
      </div>
    ))}

    {[...block.inlineCallouts, ...block.trailingCallouts].map(
      (callout, idx) => (
        <CalloutParagraph
          key={`docref-callout-${block.id ?? index}-${idx}`}
          callout={callout}
        />
      ),
    )}
  </div>
);

const StructuredDocumentItem = ({
  item,
  index,
}: {
  item: PresentedDocumentReferenceItem;
  index: number;
}) => (
  <article className="flex flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white">
    <header className="flex flex-col gap-1 bg-slate-50 px-4 py-3">
      <p className="text-[11px] leading-tight font-semibold text-slate-800">
        {item.caption.heading}
      </p>
      {item.caption.timestamp ? (
        <p className="text-[10px] leading-tight text-slate-500">
          {item.caption.timestamp}
        </p>
      ) : null}
      {item.caption.detail ? (
        <p className="text-[10px] leading-tight text-slate-500">
          {item.caption.detail}
        </p>
      ) : null}
    </header>
    <div className="flex flex-col gap-4 px-4 py-4">
      {item.blocks.length === 0 ? (
        <p className="text-[10px] leading-tight text-slate-500">
          No narrative content was available for this entry.
        </p>
      ) : (
        item.blocks.map((block, blockIdx) => (
          <StructuredBlock
            key={`docref-item-${index}-block-${blockIdx}`}
            block={block}
            index={blockIdx}
          />
        ))
      )}
      {item.footnotes.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-slate-200 pt-3">
          {item.footnotes.map((footnote, idx) => (
            <CalloutParagraph
              key={`docref-item-footnote-${idx}`}
              callout={footnote}
            />
          ))}
        </div>
      ) : null}
    </div>
  </article>
);

export const DocumentReferenceStructuredContent = ({
  content,
}: DocumentReferenceStructuredContentProps) => {
  const presented = presentDocumentReferenceDocument(content);

  return (
    <div className="flex flex-col gap-4">
      {presented.items.map((item, idx) => (
        <StructuredDocumentItem
          key={`docref-structured-${idx}`}
          item={item}
          index={idx}
        />
      ))}
      {presented.footnotes.length > 0 ? (
        <div className="flex flex-col gap-2 rounded-sm border border-slate-200 bg-slate-50 px-4 py-3">
          {presented.footnotes.map((footnote, idx) => (
            <CalloutParagraph
              key={`docref-global-footnote-${idx}`}
              callout={footnote}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
